import { AIAgent } from "@/sharedTypes";
import { baseApi } from "../baseApi";

const aiAgentTag = "AIAgent";

export interface AIAgentUpdatePayload {
  display_name?: string;
  company_scope: string;
  ai_greeting_text: string;
  chosen_llm: string;
  // Temporal Activity Database (outages/events/activities/maintenance).
  events_enabled?: boolean;
  events_default_city?: string;
  timezone?: string;
}

export interface AIAgentCreatePayload {
  display_name: string;
  company_agent_tone: string;
  chosen_llm: string;
  company_scope: string;
  ai_greeting_text: string;
  chat_history_update_value: number;
  chat_history_update_unit: "day" | "month" | "year";
  faq_files_path?: File;
  issue_resolution_files_path?: File;
  chat_history_source_path?: File;
}

// Creation is multipart: the optional training files travel with the config in
// one request, exactly like the gateway console does it.
const toCreateFormData = (values: AIAgentCreatePayload) => {
  const body = new FormData();

  body.append("display_name", values.display_name);
  body.append("company_agent_tone", values.company_agent_tone);
  body.append("chosen_llm", values.chosen_llm);
  body.append("company_scope", values.company_scope);
  body.append("ai_greeting_text", values.ai_greeting_text);
  body.append("chat_history_update_value", String(values.chat_history_update_value));
  body.append("chat_history_update_unit", values.chat_history_update_unit);

  if (values.faq_files_path) {
    body.append("faq_files_path", values.faq_files_path);
  }
  if (values.issue_resolution_files_path) {
    body.append("issue_resolution_files_path", values.issue_resolution_files_path);
  }
  if (values.chat_history_source_path) {
    body.append("chat_history_source_path", values.chat_history_source_path);
  }

  return body;
};

const aiAgentApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAIAgent: builder.query<AIAgent | null, void>({
      query: () => "/ai-agent",
      transformResponse: (response: { data: AIAgent | null }) => response.data,
      providesTags: [aiAgentTag],
    }),
    createAIAgent: builder.mutation<{ data: AIAgent }, AIAgentCreatePayload>({
      query: (values) => ({
        url: "/ai-agent",
        method: "POST",
        // fetchBaseQuery leaves FormData untouched and lets the browser set the
        // multipart boundary itself.
        body: toCreateFormData(values),
      }),
      invalidatesTags: [aiAgentTag, "CompanyInfo"],
    }),
    updateAIAgent: builder.mutation<{ data: AIAgent }, AIAgentUpdatePayload>({
      query: (data) => ({
        url: "/ai-agent",
        method: "PUT",
        body: data,
      }),
      invalidatesTags: [aiAgentTag, "CompanyInfo"],
    }),
  }),
});

export const {
  useGetAIAgentQuery,
  useCreateAIAgentMutation,
  useUpdateAIAgentMutation,
} = aiAgentApi;
